import { callClaus } from './client';

// Every org-scoped call pins path org == companyId == organizationId (the broker enforces this).
const scope = (orgId) => ({ companyId: orgId, organizationId: orgId });

// ---- templates (global) ----
export const listTemplates = (type) =>
	callClaus(`/templates${type ? `?type=${encodeURIComponent(type)}` : ''}`, 'GET');

// ---- organization resolution / provisioning ----
export const getOrg = (orgId) => callClaus(`/organizations/${orgId}`, 'GET', undefined, scope(orgId));

// Create-with-id: pin the CLAUS org id to the ERIN companyId (preserves organizationId == companyId).
export const createOrg = (companyId, name) =>
	callClaus('/organizations', 'POST', { id: companyId, name }, { companyId });

/** Resolve the company's CLAUS org, provisioning it (id == companyId) if it doesn't exist yet. */
export async function ensureOrg(companyId, name) {
	try {
		return await getOrg(companyId);
	} catch (err) {
		// broker errors are message-only (no status code survives the 200-wrapped envelope), so
		// sniff the text — word-bounded so e.g. "40404 rows" can't read as a missing org
		if (/\b404\b|not found/i.test(err.message || '')) return createOrg(companyId, name);
		throw err;
	}
}

// ---- integrations ----
export const listIntegrations = (orgId) =>
	callClaus(`/organizations/${orgId}/integrations`, 'GET', undefined, scope(orgId));

export const getIntegration = (orgId, id) =>
	callClaus(`/organizations/${orgId}/integrations/${id}`, 'GET', undefined, scope(orgId));

export const createIntegration = (orgId, payload) =>
	callClaus(`/organizations/${orgId}/integrations`, 'POST', payload, scope(orgId));

export const patchIntegration = (orgId, id, patch) =>
	callClaus(`/organizations/${orgId}/integrations/${id}`, 'PATCH', patch, scope(orgId));

export const deleteIntegration = (orgId, id) =>
	callClaus(`/organizations/${orgId}/integrations/${id}`, 'DELETE', undefined, scope(orgId));

export const testConnection = (orgId, { templateId, inputParams, authParams }) =>
	callClaus(
		`/organizations/${orgId}/integrations/test`,
		'POST',
		{ templateId, inputParams, authParams },
		scope(orgId),
	);

// Duplicate within the same company (targetOrganizationId defaults to the current org).
export const duplicateIntegration = (orgId, id, { targetOrganizationId = orgId, name, copyCredentials = false }) =>
	callClaus(
		`/organizations/${orgId}/integrations/${id}/duplicate`,
		'POST',
		{ targetOrganizationId, name, copyCredentials },
		scope(orgId),
	);

// ---- sync ----
export const triggerSync = (orgId, id, body = {}) =>
	callClaus(`/organizations/${orgId}/integrations/${id}/sync`, 'POST', body, scope(orgId));

export const getSyncHistory = (orgId, id, { page = 1, limit = 20 } = {}) =>
	callClaus(
		`/organizations/${orgId}/integrations/${id}/sync-history?page=${page}&limit=${limit}`,
		'GET',
		undefined,
		scope(orgId),
	);

// ---- webhooks ----
export const listWebhooks = (orgId, id) =>
	callClaus(`/organizations/${orgId}/integrations/${id}/webhooks`, 'GET', undefined, scope(orgId));

export const createWebhook = (orgId, id, body) =>
	callClaus(`/organizations/${orgId}/integrations/${id}/webhooks`, 'POST', body, scope(orgId));

export const patchWebhook = (orgId, id, webhookId, body) =>
	callClaus(
		`/organizations/${orgId}/integrations/${id}/webhooks/${webhookId}`,
		'PATCH',
		body,
		scope(orgId),
	);

// ---- SFTP credentials ----
export const listSftpCredentials = (orgId) =>
	callClaus(`/organizations/${orgId}/sftp-credentials`, 'GET', undefined, scope(orgId));

export const createSftpCredential = (orgId, body) =>
	callClaus(`/organizations/${orgId}/sftp-credentials`, 'POST', body, scope(orgId));
